import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { InputErrorMessages } from "@/shared/components/errors/input-error-messages";
import { Address } from "@/services/backend/addresses/types/address";

interface CustomerAddressCardProps {
  customerAddress: Address;
  onCustomerAddressChange: (customerAddress: Address) => void;
  title?: string;
  description?: string;
  fieldErrors?: { [key: string]: string[] };
  resetFieldError?: (field: string) => void;
  disabled?: boolean;
}

export function CustomerAddressCard({
  customerAddress,
  onCustomerAddressChange,
  fieldErrors = undefined,
  resetFieldError = undefined,
  title = t("Kundenadresse"),
  description = t("Adresse des Kunden"),
  disabled = false,
}: CustomerAddressCardProps) {
  const errorMessages = (field: string): string[] => {
    if (!field || field === "") {
      return [];
    }

    return fieldErrors ? (fieldErrors[field] ?? []) : [];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="company">{t("Firma")}</Label>
          <Input
            type="text"
            id="company"
            className={
              errorMessages("company").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Firma")}
            value={customerAddress.company}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("company");
              }
              onCustomerAddressChange({
                ...customerAddress,
                company: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("company")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="department">{t("Abteilung")}</Label>
          <Input
            type="text"
            id="department"
            className={
              errorMessages("department").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Abteilung")}
            value={customerAddress.department}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("department");
              }
              onCustomerAddressChange({
                ...customerAddress,
                department: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("department")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="contact">{t("Ansprechpartner")}</Label>
          <Input
            type="text"
            id="contact"
            className={
              errorMessages("contact").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Ansprechpartner")}
            value={customerAddress.contact}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("contact");
              }
              onCustomerAddressChange({
                ...customerAddress,
                contact: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("contact")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="street">{t("Straße")}</Label>
          <Input
            type="text"
            id="street"
            className={
              errorMessages("street").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Straße")}
            value={customerAddress.street}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("street");
              }
              onCustomerAddressChange({
                ...customerAddress,
                street: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("street")} />
        </div>
        <div className="flex space-x-4">
          <div className="grid max-w-xs items-center gap-1.5">
            <Label htmlFor="postalCode">{t("PLZ")}</Label>
            <Input
              type="text"
              id="postalCode"
              className={
                errorMessages("postalCode").length > 0
                  ? "border-red-500 shadow-md"
                  : ""
              }
              placeholder={t("PLZ")}
              value={customerAddress.postalCode}
              onChange={(e) => {
                if (resetFieldError) {
                  resetFieldError("postalCode");
                }
                onCustomerAddressChange({
                  ...customerAddress,
                  postalCode: e.target.value,
                });
              }}
              disabled={disabled}
            />
            <InputErrorMessages errors={errorMessages("postalCode")} />
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="city">{t("Stadt")}</Label>
            <Input
              type="text"
              id="city"
              className={
                errorMessages("city").length > 0
                  ? "border-red-500 shadow-md"
                  : ""
              }
              placeholder={t("Stadt")}
              value={customerAddress.city}
              onChange={(e) => {
                if (resetFieldError) {
                  resetFieldError("city");
                }
                onCustomerAddressChange({
                  ...customerAddress,
                  city: e.target.value,
                });
              }}
              disabled={disabled}
            />
            <InputErrorMessages errors={errorMessages("city")} />
          </div>
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="country">{t("Land")}</Label>
          <Input
            type="text"
            id="country"
            className={
              errorMessages("country").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Land")}
            value={customerAddress.country}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("country");
              }
              onCustomerAddressChange({
                ...customerAddress,
                country: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("country")} />
        </div>
      </CardContent>
    </Card>
  );
}
